export const DOMAIN_PATTERN = /^(?=.{1,253}$)(?!-)(?:[a-zA-Z0-9-]{1,63}\.)+[a-zA-Z]{2,63}$/

export const DOMAIN_TYPES = [
  'live', 
  // 'shop'
]

export const DOMAIN_TYPE_LABELS = {
  live: '直播域名',
  // shop: '商城域名',
}

export const DEFAULT_DNS_RECORDS = []

function pickFirst(source, keys) {
  for (const key of keys) {
    const value = source?.[key]
    if (value !== undefined && value !== null && value !== '') {
      return value
    }
  }
  return ''
}

function toArray(value) {
  return Array.isArray(value) ? value : []
}

function cloneDnsRecords(records) {
  return toArray(records).map(item => ({ ...item }))
}

function extractDomainRecordId(source) {
  // 不同接口返回的主键名不稳定，这里统一做宽松兼容，避免“页面有域名但取不到记录 id”。
  return pickFirst(source, [
    'id',
    'domain_id',
    'bind_id',
    'domain_bind_id',
    'domainBindId',
    'commercial_domain_id',
    'commercial_domain_bind_id',
    'commercialDomainId',
    'commercialDomainBindId',
  ])
}

export function normalizeDomainInput(value) {
  return String(value || '')
    .trim()
    .replace(/^https?:\/\//i, '')
    .replace(/\/.*$/, '')
    .toLowerCase()
}

export function validateDomainInput(value, label = '域名') {
  const domain = normalizeDomainInput(value)

  if (!domain) {
    return `请输入${label}`
  }

  if (!DOMAIN_PATTERN.test(domain)) {
    return `请输入正确的${label}格式，例如 shop.example.com`
  }

  return ''
}

export function normalizeDomainType(value) {
  const normalized = String(value || '').trim().toLowerCase()
  return DOMAIN_TYPES.includes(normalized) ? normalized : ''
}

export function domainTypeLabel(type) {
  return DOMAIN_TYPE_LABELS[normalizeDomainType(type)] || '域名'
}

function normalizeDnsRecord(record) {
  if (!record || typeof record !== 'object') return null

  const type = String(
    pickFirst(record, ['type', 'record_type', 'recordType', 'dns_type']) || ''
  )
    .trim()
    .toUpperCase()
  const host = String(
    pickFirst(record, ['host', 'name', 'rr', 'record', 'subdomain']) || ''
  ).trim()
  const value = String(
    pickFirst(record, ['value', 'content', 'target', 'data', 'points_to']) || ''
  ).trim()
  const remark = String(
    pickFirst(record, ['remark', 'description', 'tips', 'title']) || ''
  ).trim()

  if (!type && !host && !value) return null

  return {
    type: type || '-',
    host: host || '@',
    value: value || '-',
    remark: remark || '',
  }
}

function buildDnsRecordsFromBindRecord(payload = {}, fallbackType = '') {
  const domainType = normalizeDomainType(
    pickFirst(payload, ['domain_type', 'domainType', 'type']) || fallbackType
  )
  const txtRecord = String(pickFirst(payload, ['txt_record', 'txtRecord']) || '').trim()
  const cnameRecord = String(
    pickFirst(payload, ['cname_record', 'cnameRecord', 'domain_cname_value', 'cname_value']) || ''
  ).trim()

  if (!txtRecord && !cnameRecord) {
    return []
  }

  return [
    {
      type: 'TXT',
      host: '@',
      value: txtRecord || '-',
      remark: '',
    },
    {
      type: 'CNAME',
      host: domainType || '-',
      value: cnameRecord || '-',
      remark: '',
    },
  ]
}

export function extractDnsRecords(payload) {
  const bindRecords = buildDnsRecordsFromBindRecord(payload || {})
  if (bindRecords.length) {
    return bindRecords
  }

  const candidates = [
    payload?.dns_records,
    payload?.dnsRecords,
    payload?.records,
    payload?.record_list,
    payload?.recordList,
    payload?.dns,
    payload?.resolve_records,
    payload?.resolveRecords,
    payload?.data?.dns_records,
    payload?.data?.records,
  ]

  for (const item of candidates) {
    const records = toArray(item).map(normalizeDnsRecord).filter(Boolean)
    if (records.length) {
      return records
    }
  }

  return []
}

function inferVerified(payload = {}) {
  const directFlag = pickFirst(payload, [
    'verified',
    'is_verified',
    'verify_success',
    'verifyStatus',
  ])

  if (typeof directFlag === 'boolean') return directFlag
  if (String(directFlag) === '1') return true
  if (String(directFlag).toLowerCase() === 'true') return true

  // 后端有时返回布尔态，有时只返回 0/1 的数字态；这里统一做“是否已验证”的兜底判断。
  const numericFlags = [
    pickFirst(payload, ['dns_status', 'dnsStatus']),
    pickFirst(payload, ['ssl_status', 'sslStatus']),
    pickFirst(payload, ['status', 'domain_status', 'verify_status', 'domainStatus']),
  ]

  if (numericFlags.some(flag => String(flag).trim() === '1')) {
    return true
  }

  const status = String(
    pickFirst(payload, ['dns_status', 'dnsStatus', 'status', 'domain_status', 'verify_status', 'domainStatus']) || ''
  ).trim().toLowerCase()

  return ['verified', 'success', 'active', 'resolved', 'online', 'bound', '1'].includes(status)
}

export function domainStatusText(status, verified = false) {
  const normalized = String(status || '').trim().toLowerCase()

  if (verified || ['verified', 'success', 'active', 'resolved', 'online', '1'].includes(normalized)) {
    return '已验证'
  }

  if (['0', 'pending', 'processing', 'checking', 'waiting'].includes(normalized)) {
    return '待验证'
  }

  if (normalized) {
    return '待验证'
  }

  return '未绑定'
}

export function createEmptyDomainRecord(domainType) {
  const type = normalizeDomainType(domainType)
  return {
    id: '',
    domain: '',
    domainType: type,
    domain_type: type,
    status: '',
    verified: false,
    verifyMessage: '',
    dnsRecords: [],
  }
}

export function createEmptyDomainRecordsMap() {
  return DOMAIN_TYPES.reduce((acc, type) => {
    acc[type] = createEmptyDomainRecord(type)
    return acc
  }, {})
}

export function normalizeDomainRecord(record = {}, fallbackType = '') {
  const source = record?.data && typeof record.data === 'object' ? record.data : record
  const domainType = normalizeDomainType(
    pickFirst(source, ['domain_type', 'domainType', 'type', 'bind_type', 'bindType']) || fallbackType
  )
  const verified = inferVerified(source)
  const domain = normalizeDomainInput(
    pickFirst(source, ['domain', 'site_domain', 'bind_domain', 'current_domain', 'domain_name'])
  )
  const status = String(
    pickFirst(source, ['dns_status', 'dnsStatus', 'domain_status', 'verify_status', 'status', 'domainStatus']) || ''
  ).trim()
  const verifyMessage = String(
    pickFirst(source, ['verify_message', 'message', 'msg', 'tips', 'remark']) || ''
  ).trim()

  return {
    id: extractDomainRecordId(source),
    domain,
    domainType,
    domain_type: domainType,
    status: status || (verified ? 'verified' : domain ? 'pending' : ''),
    verified,
    verifyMessage,
    dnsRecords: extractDnsRecords({
      ...source,
      domain_type: domainType || pickFirst(source, ['domain_type', 'domainType', 'type']),
    }),
  }
}

export function mergeDomainRecord(currentRecord, nextRecord) {
  const baseType =
    normalizeDomainType(nextRecord?.domainType || nextRecord?.domain_type) ||
    normalizeDomainType(currentRecord?.domainType || currentRecord?.domain_type)
  const base = createEmptyDomainRecord(baseType)

  return {
    ...base,
    ...(currentRecord || {}),
    ...(nextRecord || {}),
    domainType: baseType,
    domain_type: baseType,
    dnsRecords:
      toArray(nextRecord?.dnsRecords).length > 0
        ? cloneDnsRecords(nextRecord.dnsRecords)
        : toArray(currentRecord?.dnsRecords).length > 0
          ? cloneDnsRecords(currentRecord.dnsRecords)
          : [],
  }
}

export function extractDomainList(payload) {
  if (Array.isArray(payload)) return payload
  if (Array.isArray(payload?.data)) return payload.data
  if (Array.isArray(payload?.list)) return payload.list
  if (Array.isArray(payload?.items)) return payload.items
  if (Array.isArray(payload?.rows)) return payload.rows
  if (Array.isArray(payload?.data?.data)) return payload.data.data
  return []
}

export function buildDomainRecordsMap(payload) {
  const map = createEmptyDomainRecordsMap()
  const rows = extractDomainList(payload)

  // list 接口返回的是多条绑定记录，这里按 domain_type 折叠成固定的 live/shop 两个槽位，
  // 方便页面直接按类型渲染，而不是每次都在模板里做查找。
  rows.forEach(item => {
    const normalized = normalizeDomainRecord(item)
    if (!normalized.domainType) return
    map[normalized.domainType] = mergeDomainRecord(map[normalized.domainType], normalized)
  })

  return map
}

export function summarizeDomainRecords(recordsMap = {}) {
  const safeMap = createEmptyDomainRecordsMap()

  DOMAIN_TYPES.forEach(type => {
    safeMap[type] = mergeDomainRecord(safeMap[type], recordsMap[type])
  })

  return {
    recordsMap: safeMap,
    allSaved: DOMAIN_TYPES.every(type => !!safeMap[type].id),
    allVerified: DOMAIN_TYPES.every(type => !!safeMap[type].verified),
  }
}
